import type pino from 'pino';

export type Logger = pino.Logger;

export type LoggerOptions = pino.LoggerOptions & {
  logPath?: string;
  silent?: boolean;
};

// Keep track of a logger's children so that we can
// A) keep the child log level in sync with the parent log level
// B) avoid creating multiple children with the same bindings
export function trackChildLoggers(logger: Logger) {
  const children: Record<string, Logger> = {};

  logger.on('level-change', function _updateChildren(this: Logger, level, levelValue) {
    if (this === logger) {
      if (levelValue !== undefined) {
        Object.values(children).forEach((child) => {
          child.level = level;
        });
      }
      return children;
    }
  });

  const makeChildLogger = logger.child.bind(logger);

  // @ts-ignore we're intentionally making child() return the same type as
  // pino(), so that child and parent loggers can be passed around interchangeably.
  logger.child = (bindings: pino.Bindings, childOptions: pino.LoggerOptions = {}): Logger => {
    // If this child already exists, don't create a new one.
    let childId = bindings.name;
    if (!childId) {
      try {
        childId = JSON.stringify(bindings);
      } catch (err) {
        childId = 'rjs';
      }
    }
    let child = children[childId];
    if (!child) {
      child = makeChildLogger(bindings, childOptions);
      children[childId] = child;
    }
    return child;
  };
}

// This isn't pretty, but it allows us to make logger children accessible
// without extending the pino.Logger type. This keeps our Logger portable
// and interoperable with anything that accepts a pino logger.
export function getLoggerChildren(logger: Logger): Record<string, Logger> {
  // In trackChildLoggers, we added a level-change listener that returns the logger's children.
  const listeners = logger.listeners('level-change');
  // Find that event listener...
  const childListener = listeners.find((listener) => listener.name === '_updateChildren');
  // and call it.
  if (childListener) {
    const children = childListener.call(logger, logger.level);
    return children;
  }

  return {};
}

export function getChild(logger: Logger, childName: string) {
  return getLoggerChildren(logger)[childName];
}
